// src/components/HistoryPanel.tsx
//
// Collapsible in-context "who & when" change log for an actionable entity
// (a SKU, a container). Lazy-loads its audit history the first time it's
// expanded, so the extra round-trip only happens when the user actually wants
// it. Visible to any tenant user on the page — the agent projects raw audit
// rows into friendly summaries with no secrets.
//
// occurredAt comes from the agent as UTC without a 'Z'; fmtLocalTime handles
// the fix-up (see lib/formatTime.ts).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, ChevronRight, RefreshCw, User } from 'lucide-react';
import type { AuditHistoryEvent } from '../types';
import { fmtLocalTime } from '../lib/formatTime';

interface Props {
  /** Stable react-query key for this entity's history. */
  queryKey: (string | number)[];
  /** Fetches the history rows (called only once the panel is open). */
  fetcher: () => Promise<AuditHistoryEvent[]>;
  /** Section heading. Defaults to "History". */
  title?: string;
  /** Start expanded. Defaults to false (collapsed, lazy). */
  defaultOpen?: boolean;
}

export default function HistoryPanel({ queryKey, fetcher, title = 'History', defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);

  const q = useQuery({
    queryKey,
    queryFn: fetcher,
    enabled: open,          // lazy — no fetch until the panel is opened
    staleTime: 30_000,
  });

  const rows = q.data ?? [];

  return (
    <div className="rounded-md border border-slate-700/40 bg-slate-900/30">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800/30"
      >
        <History size={14} className="text-blue-300" /> {title}
        {open && rows.length > 0 && (
          <span className="text-[10px] font-normal text-slate-500">{rows.length} change{rows.length === 1 ? '' : 's'}</span>
        )}
        <ChevronRight size={12} className={`ml-auto transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 text-xs">
          {q.isLoading && (
            <div className="flex items-center gap-2 py-3 text-slate-500">
              <RefreshCw size={12} className="animate-spin" /> Loading history…
            </div>
          )}
          {q.error && (
            <div className="py-3 text-red-300/80">Couldn't load history: {(q.error as Error).message}</div>
          )}
          {!q.isLoading && !q.error && rows.length === 0 && (
            <div className="py-3 text-slate-500">No changes recorded yet.</div>
          )}
          {!q.isLoading && !q.error && rows.length > 0 && (
            <ol className="relative border-l border-slate-700/50 ml-1.5 mt-1 space-y-2.5">
              {rows.map((ev) => (
                <li key={ev.id} className="ml-3">
                  <span className="absolute -left-[5px] mt-1 h-2 w-2 rounded-full bg-blue-400/70 border border-slate-900" />
                  <div className="text-slate-200">{ev.summary}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <User size={10} /> {ev.actor || 'unknown'}
                    </span>
                    <span className="text-slate-600">·</span>
                    <span className="tabular-nums">{fmtLocalTime(ev.occurredAt)}</span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
